import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import { ServerConfig } from 'config'
import axios from 'axios'
import { useSelector } from 'react-redux'

import Select from 'react-select'
import { PDFExport } from "@progress/kendo-react-pdf"
import PageTemplate from "./pageTemplate"
import {utc_timestamp_to_local_datetime_string } from "../../utils/datetime_utc_local";
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Grid, Button, CircularProgress } from '@material-ui/core'

import Tab from '@material-ui/core/Tab'
import TabContext from '@material-ui/lab/TabContext'
import TabList from '@material-ui/lab/TabList'
import TabPanel from '@material-ui/lab/TabPanel'

import _ from 'lodash'
import logo_mono from 'assets/img/logo/logo_circle_mono.png'

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    fontWeight: 'bold',
    borderBottom: '2px solid black',
    paddingBottom: '4px',
    marginBottom: '4px',
  },
  tableRow: {
    borderBottom: '1px solid #ccc',
    padding: '4px 0',
  },
  tableCell: {
    padding: '4px 8px',
  },
}))

// ── helpers ───────────────────────────────────────────────────────────────────

function monthToTimestamps(year, month) {
  const start = new Date(year, month, 1, 0, 0, 0, 0)
  const end   = new Date(year, month + 1, 0, 23, 59, 59, 999)
  return { start_ts_utc: start.getTime(), end_ts_utc: end.getTime() }
}

function formatWeight(val) {
  if (val == null) return '—'
  return parseFloat(val).toFixed(1)
}

// Full DORIIS material list — left column (wds: false), right column (wds: true)
const MATERIAL_MAP = [
  // Left column — matches DORIIS order
  { label: 'Aluminum',              field: 'Aluminum weight',       wds: false },
  { label: 'Glass',                 field: 'Glass weight',          wds: false },
  { label: 'Bi-Metal',             field: 'Bi-Metal weight',       wds: false },
  { label: 'Plastic PETE (1)',      field: 'PETE weight',           wds: false },
  { label: 'Plastic HDPE (2)',     field: 'HDPE weight',           wds: false },
  { label: 'RVM Aluminum',          field: 'RVM Aluminum weight',   wds: false },
  { label: 'RVM Glass',             field: 'RVM Glass weight',      wds: false },
  { label: 'RVM Plastic PETE (1)',  field: 'RVM PETE weight',       wds: false },
  { label: 'RVM Plastic HDPE (2)', field: 'RVM HDPE weight',       wds: false },
  // Right column — matches DORIIS order
  { label: 'Plastic Vinyl (3)',     field: 'Vinyl weight',          wds: true },
  { label: 'Plastic LDPE (4)',     field: 'LDPE weight',           wds: true },
  { label: 'Plastic PP (5)',       field: 'PP weight',             wds: true },
  { label: 'Plastic PS (6)',       field: 'PS weight',             wds: true },
  { label: 'Plastic Other (7)',    field: 'OTHER weight',          wds: true },
  { label: 'WDS Bag-in-Box',       field: 'Bag-in-Box weight',     wds: true },
  { label: 'WDS Multi-Layer Pouch',field: 'Multilayer-Pouch weight', wds: true },
  { label: 'WDS Paperboard Carton',field: 'Paperboard-Carton weight', wds: true },
  { label: 'Plastic WDS-BBP',      field: 'WDS-BBP weight',        wds: true },
]

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

function buildMonthOptions() {
  return MONTHS.map((m, i) => ({ value: i, label: m }))
}

function buildYearOptions() {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push({ value: y, label: String(y) })
  }
  return years
}

// ── component ─────────────────────────────────────────────────────────────────

const DR14 = () => {
  const ref = React.createRef()
  const classes = useStyles()

  const token  = useSelector(state => state.auth.access_token)
  const tenant = useSelector(state => state.auth.tenant)

  // tabs
  const [tabValue, setTabValue] = useState('1')

  const now = new Date()
  const monthOptions = buildMonthOptions()
  const yearOptions  = buildYearOptions()

  // cert IDs
  const [availableCertIDs,    setAvailableCertIDs]    = useState([])
  const [selectedCertID,      setSelectedCertID]      = useState({ label: 'Please Select', value: '' })
  const [selectedCertID_prev, setSelectedCertID_prev] = useState({ label: 'Please Select', value: '' })

  // new report selections
  const [selectedMonth, setSelectedMonth] = useState({ value: now.getMonth(),    label: MONTHS[now.getMonth()] })
  const [selectedYear,  setSelectedYear]  = useState({ value: now.getFullYear(), label: String(now.getFullYear()) })

  // previous reports filters
  const [prevMonth, setPrevMonth] = useState({ value: now.getMonth(),    label: MONTHS[now.getMonth()] })
  const [prevYear,  setPrevYear]  = useState({ value: now.getFullYear(), label: String(now.getFullYear()) })

  // report state
  const [DR14Data,  setDR14Data]  = useState({})
  const [DR14List,  setDR14List]  = useState([])
  const [allowSave, setAllowSave] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // per-row DORIIS download state, keyed by dr14 report id, so only the
  // clicked row shows a spinner instead of the whole list
  const [doriisDownloadingId, setDoriisDownloadingId] = useState(null)

  // ── effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchCertIDList(token)
    fetchDR14List(token)
  }, [token])

  useEffect(() => {
    setAllowSave(false)
    setDR14Data({})
    fetchDR14List(token)
  }, [selectedCertID_prev, prevMonth, prevYear])

  // ── API helpers ───────────────────────────────────────────────────────────────

  function authConfig() {
    const config = { headers: { 'Content-Type': 'application/json' } }
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  }

  async function fetchCertIDList(token) {
    const res = await axios.get(`${ServerConfig.SERVER_URL}/tenant/cert_ids`, authConfig())
    const options = []
    for (let idx in res.data) {
      options.push({ value: res.data[idx], label: res.data[idx] })
    }
    setAvailableCertIDs(options)
    if (options.length > 0) {
      setSelectedCertID(options[0])
      setSelectedCertID_prev(options[0])
    }
  }

  async function generateDR14() {
    setIsLoading(true)

    try {
      const { start_ts_utc, end_ts_utc } = monthToTimestamps(selectedYear.value, selectedMonth.value)

      const res = await axios.post(
        `${ServerConfig.SERVER_URL}/tenant/dr14`,
        {
          cert_id:      selectedCertID.value,
          start_ts_utc,
          end_ts_utc,
        },
        authConfig()
      )

      const total = res.data
      if (!total) {
        alert('No data found for the selected period.')
        setIsLoading(false)
        return
      }

      setDR14Data({
        ...total,
        month_label:             `${selectedMonth.label} ${selectedYear.value}`,
        month_num:               selectedMonth.value + 1,
        month:                   selectedMonth.label,
        year:                    selectedYear.value,
        start_ts:                start_ts_utc,
        end_ts:                  end_ts_utc,
        ts_create:               Date.now(),
        cert_numbers:            selectedCertID.value,
        tenant_business_name:    tenant?.business_name || '',
        tenant_address_street:   tenant?.street        || '',
        tenant_city_state_zip:   `${tenant?.city || ''}, ${tenant?.state || ''}, ${tenant?.zipcode || ''}`,
        tenant_contact_name:     tenant?.first_name    || '',
      })
      setAllowSave(true)
    } catch (err) {
      console.error('Failed to generate DR14', err)
      alert('Error generating DR-14 report.')
    }

    setIsLoading(false)
  }

  function mark_saved() {
    setIsLoading(true)

    axios.post(
      `${ServerConfig.SERVER_URL}/tenant/dr14_save`,
      {
        cert_id:      selectedCertID.value,
        month_num:    DR14Data.month_num,
        year:         DR14Data.year,
        start_ts_utc: DR14Data.start_ts,
        end_ts_utc:   DR14Data.end_ts,
      },
      authConfig()
    ).then(() => {
      fetchDR14List(token)
    })

    setIsLoading(false)
  }

  function fetchDR14List(token) {
    const { start_ts_utc, end_ts_utc } = monthToTimestamps(prevYear.value, prevMonth.value)

    axios.post(
      `${ServerConfig.SERVER_URL}/tenant/dr14_list`,
      {
        cert_id:      selectedCertID_prev.value,
        start_ts_utc,
        end_ts_utc,
      },
      authConfig()
    ).then((res) => {
      setDR14List(Array.isArray(res.data) ? res.data : [])
    }).catch(() => {
      setDR14List([])
    })
  }

  function fetchDR14ById(dr14_id) {
    axios.post(
      `${ServerConfig.SERVER_URL}/tenant/dr14_load`,
      { id: dr14_id },
      authConfig()
    ).then((res) => {
      setAllowSave(false)
      setDR14Data(res.data)
    })
  }

  // downloads the DORIIS Handling Fee .txt for a single saved DR14 report,
  // same blob-download pattern used elsewhere (e.g. Daily Summaries Excel export)
  async function downloadDoriisReport(dr14_id) {
    setDoriisDownloadingId(dr14_id)

    try {
      const res = await axios.post(
        `${ServerConfig.SERVER_URL}/tenant/dr14_doriis_export`,
        { id: dr14_id },
        {
          ...authConfig(),
          responseType: 'blob',
        }
      )

      let filename = `DORIIS_DR14_${dr14_id}.txt`
      const disposition = res.headers['content-disposition']
      if (disposition) {
        const match = disposition.split(/;(.+)/)[1]?.split(/=(.+)/)[1]
        if (match) {
          filename = match.replace(/['"]/g, '')
        }
      }

      const blob = new Blob([res.data], { type: 'text/plain' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download DORIIS report', err)
      alert('Error downloading DORIIS report.')
    }

    setDoriisDownloadingId(null)
  }

  // ── tab switch ────────────────────────────────────────────────────────────────

  const handleChange = (event, newValue) => {
    setDR14Data({})
    setAllowSave(false)
    setTabValue(newValue)
  }

  if (!token) return <Redirect to="/login" />

  // ── render helpers ────────────────────────────────────────────────────────────

  const crvMaterials = MATERIAL_MAP.filter(m => !m.wds)
  const wdsMaterials = MATERIAL_MAP.filter(m => m.wds)

  function renderWeight(field) {
    if (!DR14Data || !field) return '—'
    const val = DR14Data[field]
    if (val == null) return '—'
    return parseFloat(val).toFixed(1)
  }

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <>
      <TabContext value={tabValue} style={{'marginLeft': '0', 'paddingLeft': '0', 'marginRight': '0', 'paddingRight': '0'}}>
        <TabList onChange={handleChange} aria-label="DR14-Subpages">
          <Tab label="Previous Reports" value="1" />
          <Tab label="New Report"       value="2" />
        </TabList>

        {/* ── TAB 1: Previous Reports ── */}
        <TabPanel value="1" style={{'marginLeft': '0', 'paddingLeft': '0', 'marginRight': '0', 'paddingRight': '0'}}>
          <Grid container spacing={3} style={{'borderRadius': '20px', 'backgroundColor': 'white', 'padding': '20px'}}>
            <Grid xs={12}>
              <b>Previously generated Reports:</b>
            </Grid>

            <Grid container spacing={3} style={{'padding': '10px'}}>
              <Grid xs={12} style={{'marginBottom': '20px', 'paddingRight': '20px'}}>
                <Typography variant="h2">Cert-ID:</Typography>
                {availableCertIDs.length > 0 &&
                  <Select
                    name="cert_id_prev"
                    id="cert_id_prev"
                    onChange={(e) => setSelectedCertID_prev(e)}
                    options={availableCertIDs}
                    defaultValue={selectedCertID_prev}
                  />
                }
              </Grid>

              <Grid xs={5} style={{'marginBottom': '20px'}}>
                <Typography variant="h2">Month:</Typography>
                <Select
                  options={monthOptions}
                  value={prevMonth}
                  onChange={(e) => setPrevMonth(e)}
                />
              </Grid>

              <Grid xs={5} style={{'marginBottom': '20px'}}>
                <Typography variant="h2">Year:</Typography>
                <Select
                  options={yearOptions}
                  value={prevYear}
                  onChange={(e) => setPrevYear(e)}
                />
              </Grid>
            </Grid>

            <Grid xs={12} style={{'marginTop': '20px'}}>{/* spacing */}</Grid>
            <Grid xs={12}><b>Found {DR14List.length} reports:</b></Grid>

            {_.map(DR14List, (dr14line) => (
              <Grid container key={dr14line.id} alignItems="center">
                <Grid xs={4}>
                  {utc_timestamp_to_local_datetime_string(dr14line.ts_created)}
                </Grid>
                <Grid xs={3}>
                {new Date(dr14line.ts_from).toLocaleString('default', { month: 'long' })} {new Date(dr14line.ts_from).getFullYear()}
                </Grid>
                <Grid xs={2}>
                  {dr14line.cert_id}
                </Grid>
                <Grid xs={2}
                  onClick={() => fetchDR14ById(dr14line.id)}
                  style={{'cursor': 'pointer'}}
                >
                  Load Report
                </Grid>
                <Grid xs={1}
                  onClick={() => {
                    if (doriisDownloadingId !== dr14line.id) downloadDoriisReport(dr14line.id)
                  }}
                  style={{'cursor': doriisDownloadingId === dr14line.id ? 'default' : 'pointer'}}
                >
                  {doriisDownloadingId === dr14line.id
                    ? <CircularProgress size={16} />
                    : 'DORIIS Report'
                  }
                </Grid>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* ── TAB 2: New Report ── */}
        <TabPanel value="2" style={{'marginLeft': '0', 'paddingLeft': '0', 'marginRight': '0', 'paddingRight': '0'}}>
          <Grid container spacing={3} style={{'borderRadius': '20px', 'backgroundColor': 'white', 'padding': '20px'}}>

            <Grid xs={12} style={{'marginBottom': '20px', 'paddingRight': '20px'}}>
              <Typography variant="h2">Cert-ID:</Typography>
              {availableCertIDs.length > 0 &&
                <Select
                  name="cert_id"
                  id="cert_id"
                  onChange={(e) => { setSelectedCertID(e); setAllowSave(false); setDR14Data({}) }}
                  options={availableCertIDs}
                  defaultValue={availableCertIDs[0]}
                />
              }
            </Grid>

            <Grid xs={4} style={{'marginBottom': '20px'}}>
              <Typography variant="h2">Month:</Typography>
              <Select
                options={monthOptions}
                value={selectedMonth}
                onChange={(e) => { setSelectedMonth(e); setAllowSave(false); setDR14Data({}) }}
              />
            </Grid>

            <Grid xs={4} style={{'marginBottom': '20px'}}>
              <Typography variant="h2">Year:</Typography>
              <Select
                options={yearOptions}
                value={selectedYear}
                onChange={(e) => { setSelectedYear(e); setAllowSave(false); setDR14Data({}) }}
              />
            </Grid>

            <Grid xs={12} style={{'paddingTop': '20px', 'textAlign': 'right'}}>
              {isLoading
                ? <CircularProgress size={24} />
                : <Button onClick={generateDR14}>Generate DR-14</Button>
              }
            </Grid>

          </Grid>
        </TabPanel>
      </TabContext>

      {/* ── PDF Export (below both tabs, same as DR6) ── */}
      <Grid xs={12} style={{'marginTop': '30px'}}>
        {DR14Data.hasOwnProperty('ts_create') &&
          <>
            {allowSave &&
              <Button onClick={() => {
                if (ref.current) {
                  mark_saved()
                  ref.current.save()
                }
              }}>Save &amp; Download PDF</Button>
            }
            {!allowSave &&
              <Button onClick={() => {
                if (ref.current) ref.current.save()
              }}>Download PDF</Button>
            }

            <PDFExport
              scale={0.5}
              pageTemplate={PageTemplate}
              forcePageBreak=".page-break"
              multiPage="true"
              paperSize="letter"
              fileName={`DR14_${DR14Data.ts_create}_${DR14Data.cert_numbers}.pdf`}
              margin={{top: '1.5cm', bottom: '2.5cm', right: '0.5cm', left: '0.5cm'}}
              ref={ref}
            >
              <Grid container spacing={3}
                style={{'padding': '50px', 'border': '1px solid black', 'backgroundColor': 'white'}}
              >
                {/* Header */}
                <Grid xs={8}>
                  <h1 style={{'marginBottom': '30px'}}>DR-14 Handling Fee Report</h1>
                </Grid>
                <Grid xs={4} style={{'textAlign': 'right', 'paddingTop': '30px'}}>
                  <img src={logo_mono} height="40" alt="logo" />
                </Grid>
 
                {/* Created timestamp */}
                <Grid xs={12} style={{'marginBottom': '20px', 'borderTop': '1px solid gray', 'paddingTop': '10px'}}>
                  Created: {utc_timestamp_to_local_datetime_string(DR14Data.ts_create)}<br/>
                </Grid>
 
                {/* Shipper block */}
                <Grid xs={3}>
                  Cert-ID:<br/><br/>
                  Business-Name:<br/>
                  Address:<br/><br/><br/>
                  Contact:
                </Grid>
                <Grid xs={7}>
                  {DR14Data.cert_numbers}<br/><br/>
                  {DR14Data.tenant_business_name}<br/>
                  {DR14Data.tenant_address_street}<br/>
                  {DR14Data.tenant_city_state_zip}<br/><br/>
                  {DR14Data.tenant_contact_name}
                </Grid>
 
                <Grid xs={12} style={{'borderTop': '1px solid gray', 'marginTop': '10px', 'marginBottom': '10px'}}>
                  {/* spacing */}
                </Grid>
 
                {/* Month + Year on same line, matching DORIIS */}
                <Grid xs={3}><b>Month:</b> {MONTHS[DR14Data.month_num - 1]}</Grid>
                <Grid xs={3}><b>Year:</b> {DR14Data.year}</Grid>
                <Grid xs={6} />
 
                <Grid xs={12} style={{'marginBottom': '20px'}} />
 
                {/* Materials table header */}
                <Grid container xs={12}
                  style={{
                    'fontWeight': 'bold',
                    'borderTop': '1px solid black',
                    'borderBottom': '1px solid black',
                    'paddingTop': '6px',
                    'paddingBottom': '6px',
                    'marginBottom': '0',
                    'backgroundColor': '#f5f5f5',
                  }}
                >
                  <Grid xs={4} style={{'paddingLeft': '4px'}}>Material Type</Grid>
                  <Grid xs={2} style={{'textAlign': 'right', 'paddingRight': '8px'}}>
                    Redemption<br/>Weight<br/>(Tenth of Lbs)
                  </Grid>
                  <Grid xs={1} />
                  <Grid xs={3} style={{'paddingLeft': '4px'}}>Material Type</Grid>
                  <Grid xs={2} style={{'textAlign': 'right', 'paddingRight': '8px'}}>
                    Redemption<br/>Weight<br/>(Tenth of Lbs)
                  </Grid>
                </Grid>
 
                {/* Material rows */}
                {crvMaterials.map((crv, idx) => {
                  const wds = wdsMaterials[idx] || null
                  return (
                    <Grid container xs={12} key={crv.label}
                      style={{'borderBottom': '1px solid #e0e0e0', 'padding': '4px 0'}}
                    >
                      <Grid xs={4} style={{'paddingLeft': '4px'}}>{crv.label}</Grid>
                      <Grid xs={2} style={{'textAlign': 'right', 'paddingRight': '8px'}}>
                        {crv.field ? renderWeight(crv.field) : ''}
                      </Grid>
                      <Grid xs={1} />
                      {wds
                        ? <>
                            <Grid xs={3} style={{'paddingLeft': '4px'}}>{wds.label}</Grid>
                            <Grid xs={2} style={{'textAlign': 'right', 'paddingRight': '8px'}}>
                              {wds.field ? renderWeight(wds.field) : ''}
                            </Grid>
                          </>
                        : <Grid xs={5} />
                      }
                    </Grid>
                  )
                })}
 
              </Grid>
            </PDFExport>
          </>
        }
      </Grid>
    </>
  )
}

export default DR14